/**
 * Billing Store - Instructor Accounting State Management
 *
 * Online-only (no IndexedDB cache) — Abrechnung
 * requires fresh data from the server.
 * Supports both Quarterly and Monthly billing periods.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { billingApi } from '@/api/billing.api'
import { useSessionsStore } from '@/stores/sessions.store'
import { isCurrentPeriod, getCreatablePeriods } from '@/utils/billing.utils'
import type {
  InstructorAccountSummaryDto,
  InstructorAccountDto,
  CreateInstructorAccountRequest,
} from '@/types/api.types'
import { BillingPeriodType, InstructorAccountStatus } from '@/types/api.types'

export const useBillingStore = defineStore('billing', () => {
  // ==========================================================================
  // State
  // ==========================================================================

  const accounts = ref<InstructorAccountSummaryDto[]>([])
  const currentDetail = ref<InstructorAccountDto | null>(null)
  const isLoading = ref(false)
  const isSaving = ref(false)
  const error = ref<string | null>(null)

  /** BillingPeriodType of the trainer — derived from existing accounts */
  const trainerBillingPeriodType = ref<BillingPeriodType>(BillingPeriodType.Quarterly)

  // ==========================================================================
  // Getters
  // ==========================================================================

  /** Account for the current period (or null) */
  const currentPeriodAccount = computed(
    () =>
      accounts.value.find((a) =>
        isCurrentPeriod(a.year, a.period, trainerBillingPeriodType.value)
      ) ?? null
  )

  /** All accounts sorted newest-first */
  const allAccountsSorted = computed(() =>
    [...accounts.value].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year
      return b.period - a.period
    })
  )

  /** Periods that can be created (not yet existing) */
  const creatablePeriods = computed(() => {
    const all = getCreatablePeriods(trainerBillingPeriodType.value)
    return all.filter(
      (p) => !accounts.value.some((a) => a.year === p.year && a.period === p.period)
    )
  })

  /** Find account by ID */
  const getById = computed(() => {
    return (id: number) => accounts.value.find((a) => a.id === id)
  })

  /**
   * Get the account status for a session's date (for badge display).
   * Returns the status of a submitted+ account that covers the session date, or null.
   * Works for both Quarterly and Monthly billing periods.
   */
  function getAccountStatusForDate(sessionDate: string): InstructorAccountStatus | null {
    const d = new Date(sessionDate)
    const year = d.getFullYear()
    const month = d.getMonth() + 1 // 1-based
    const quarter = Math.ceil(month / 3)

    const account = accounts.value.find((a) => {
      if (a.status < InstructorAccountStatus.Submitted) return false
      if (a.year !== year) return false
      if (a.billingPeriodType === BillingPeriodType.Monthly) {
        return a.period === month
      }
      return a.period === quarter
    })
    return account?.status ?? null
  }

  // ==========================================================================
  // Actions
  // ==========================================================================

  /** Fetch all instructor accounts */
  async function fetchAll(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      accounts.value = await billingApi.getAll()
      // Derive trainerBillingPeriodType from the trainer's actual profile.
      // The detail endpoint returns instructor.billingPeriodType (trainer's current setting).
      // This is authoritative, even if all existing accounts are a different type.
      if (accounts.value.length > 0) {
        try {
          const detail = await billingApi.getById(accounts.value[0]!.id)
          if (detail.instructor?.billingPeriodType != null) {
            trainerBillingPeriodType.value = detail.instructor.billingPeriodType
          } else {
            // Fallback: use the account's own type
            trainerBillingPeriodType.value = accounts.value[0]!.billingPeriodType
          }
        } catch {
          // Fallback: use the account's own type
          trainerBillingPeriodType.value = accounts.value[0]!.billingPeriodType
        }
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Fehler beim Laden der Abrechnungen'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch session accountings for every course in a detail response.
   * The main GET /InstructorAccounting/{id} endpoint returns sessionAccountings=[],
   * so we load them separately via /courses/{courseId}/sessions.
   */
  async function populateSessionAccountings(detail: InstructorAccountDto): Promise<void> {
    const courses = detail.courseAccountings ?? []
    await Promise.all(
      courses.map(async (ca) => {
        try {
          ca.sessionAccountings = await billingApi.getCourseSessions(detail.id, ca.courseId)
        } catch {
          ca.sessionAccountings = []
        }
      })
    )
  }

  /** Fetch detail with course accountings */
  async function fetchDetail(id: number): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const detail = await billingApi.getById(id)
      await populateSessionAccountings(detail)
      currentDetail.value = detail
      // Update trainerBillingPeriodType from instructor profile (authoritative)
      if (detail.instructor?.billingPeriodType != null) {
        trainerBillingPeriodType.value = detail.instructor.billingPeriodType
      } else {
        trainerBillingPeriodType.value = detail.billingPeriodType
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Fehler beim Laden der Abrechnungsdetails'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  /** Create a new draft account — instructorId wird vom Backend aus JWT abgeleitet */
  async function createAccount(
    year: number,
    period: number,
    billingPeriodType?: BillingPeriodType
  ): Promise<void> {
    isSaving.value = true
    error.value = null
    try {
      const request: CreateInstructorAccountRequest = { year, period }
      if (billingPeriodType != null) request.billingPeriodType = billingPeriodType
      await billingApi.create(request)
      await fetchAll()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Fehler beim Erstellen der Abrechnung'
      throw e
    } finally {
      isSaving.value = false
    }
  }

  /** Submit account (Draft → Submitted) */
  async function submitAccount(id: number): Promise<void> {
    isSaving.value = true
    error.value = null
    try {
      const updated = await billingApi.submit(id)
      await populateSessionAccountings(updated)
      currentDetail.value = updated
      await fetchAll()
      // isBilled has changed on sessions — invalidate cache so all views get fresh data
      useSessionsStore().invalidateCache()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Fehler beim Einreichen der Abrechnung'
      throw e
    } finally {
      isSaving.value = false
    }
  }

  /** Revert account (Submitted → Draft) */
  async function revertAccount(id: number): Promise<void> {
    isSaving.value = true
    error.value = null
    try {
      const updated = await billingApi.revert(id)
      await populateSessionAccountings(updated)
      currentDetail.value = updated
      await fetchAll()
      // isBilled has changed on sessions — invalidate cache so all views get fresh data
      useSessionsStore().invalidateCache()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Fehler beim Zurücknehmen der Abrechnung'
      throw e
    } finally {
      isSaving.value = false
    }
  }

  /** Recalculate all session accountings (picks up new/changed sessions) */
  async function recalculateAccount(id: number): Promise<void> {
    isSaving.value = true
    error.value = null
    try {
      const updated = await billingApi.recalculate(id)
      await populateSessionAccountings(updated)
      currentDetail.value = updated
      await fetchAll()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Fehler bei der Neuberechnung'
      throw e
    } finally {
      isSaving.value = false
    }
  }

  /** Delete a draft account */
  async function deleteAccount(id: number): Promise<void> {
    isSaving.value = true
    error.value = null
    try {
      await billingApi.delete(id)
      accounts.value = accounts.value.filter((a) => a.id !== id)
      if (currentDetail.value?.id === id) {
        currentDetail.value = null
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Fehler beim Löschen der Abrechnung'
      throw e
    } finally {
      isSaving.value = false
    }
  }

  /** Reset store state */
  function $reset(): void {
    accounts.value = []
    currentDetail.value = null
    isLoading.value = false
    isSaving.value = false
    error.value = null
    trainerBillingPeriodType.value = BillingPeriodType.Quarterly
  }

  return {
    // State
    accounts,
    currentDetail,
    isLoading,
    isSaving,
    error,
    trainerBillingPeriodType,
    // Getters
    currentPeriodAccount,
    allAccountsSorted,
    creatablePeriods,
    getById,
    // Methods
    getAccountStatusForDate,
    // Actions
    fetchAll,
    fetchDetail,
    createAccount,
    submitAccount,
    revertAccount,
    recalculateAccount,
    deleteAccount,
    $reset,
  }
})
